import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useTimer } from 'react-timer-hook';
import { useSettingsStore } from '../../store/useSettingsStore';
import { BELL_SOUND } from '../../data/constants';
import { saveCompletedBreak } from '../../lib/userSyncAuth';
import { useAuth } from '../../contexts/AuthContext';
import type { TimerType } from '../../types';

import { useSmartPIPMode } from '../../hooks/useSmartPIPMode';

import { useDeviceType } from '../../hooks/useDeviceType';

const XP_PER_MINUTE_BREAK = 1;

export const PomodoroTimer = memo(function PomodoroTimer() {
  const [timerType, setTimerType] = useState<TimerType>('pomodoro');
  const [pomodoroCount, setPomodoroCount] = useState(0);
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>('default');
  const [isFlashing, setIsFlashing] = useState(false);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isPIPMode = useSmartPIPMode(750);
  const { isMobile, isTablet } = useDeviceType();

  // Ref for audio to prevent pool exhaustion and allow pre-loading
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Only create audio if it doesn't exist
    if (!audioRef.current) {
      audioRef.current = new Audio(BELL_SOUND);
      // Pre-load
      audioRef.current.load();
    }

    return () => {
      // In StrictMode, this might run while the component is still mounted.
      // We should only clean up if we're actually unmounting.
      // However, separating "unmount" from "strictmode effect cleanup" is hard.
      // For safety in this specific case, we can rely on garbage collection
      // or clear it if we are sure. To be safe, we'll keep the cleanup but
      // the lazy init above protects against re-creation issues.
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.load(); // Detaches from audio subsystem
        audioRef.current = null;
      }
    };
  }, []);

  const { appUser, isDiscordActivity } = useAuth();

  const { timers, pomodorosBeforeLongBreak, addXP, soundEnabled, volume } = useSettingsStore();

  const getTimerDuration = useCallback((type: TimerType) => {
    const currentSettings = useSettingsStore.getState();
    switch (type) {
      case 'pomodoro':
        return currentSettings.timers.pomodoro * 60;
      case 'shortBreak':
        return currentSettings.timers.shortBreak * 60;
      case 'longBreak':
        return currentSettings.timers.longBreak * 60;
    }
  }, []);

  const getExpiryTimestamp = useCallback((seconds: number) => {
    const time = new Date();
    time.setSeconds(time.getSeconds() + seconds);
    return time;
  }, []);

  // Track timer state for proper pause/resume
  const [hasBeenStarted, setHasBeenStarted] = useState(false);
  const [pausedTimeSeconds, setPausedTimeSeconds] = useState(0);

  // Store expiry timestamp for visibility change recovery (Discord Activity fix)
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);

  // Track if timer was running before visibility change (ref persists across renders)
  const wasRunningRef = useRef(false);

  // Guard flag to prevent useEffect from interfering during user interactions
  const isUserInteracting = useRef(false);

  // Define handleTimerComplete first or via ref if needed, but since we wrap in useCallback
  // and use hoisting, we can define it later, BUT for clean mental model and no-use-before-define lint issues
  // we will define it after switchTimer and showNotification, or use a forward declaration pattern if needed.
  // Actually, handleTimerComplete depends on showNotification. So showNotification must be first.
  // And switchTimer is used by handleTimerComplete. So switchTimer must be earlier?
  // Let's check dependencies.

  // But useTimer needs onExpire which calls handleTimerComplete.
  // So handleTimerComplete must be defined BEFORE useTimer? Or we use a ref.
  // Or we just rely on the fact that onExpire is a callback called later.
  // useTimer takes a function. `onExpire: () => handleTimerComplete()`.
  // As long as handleTimerComplete is defined when the callback *fires*, it's fine.
  // However, for the dependency array of useEffect, we need the variable defined.

  // Strategy:
  // 1. switchTimer (independent-ish)
  // 2. showNotification (independent-ish)
  // 3. handleTimerComplete (depends on switchTimer, showNotification)
  // 4. useTimer (depends on handleTimerComplete... wait. useTimer is a hook. It runs on render.)

  // Problem: useTimer needs `onExpire` function. If we pass `handleTimerComplete`, `handleTimerComplete` must be defined.
  // If we pass `() => handleTimerComplete()`, the closure captures the variable. Const variables are block scoped and TDZ applies.
  // We can't define `handleTimerComplete` after `useTimer` if `useTimer` is called before.

  // Solution: Define functions before useTimer?
  // But `switchTimer` needs `restart` from `useTimer`.
  // Circular dependency!
  // `useTimer` returns `restart`. `switchTimer` calls `restart`.
  // `useTimer` accepts `onExpire`. `onExpire` calls `handleTimerComplete`.
  // `handleTimerComplete` calls `switchTimer`.

  // To break this cycle:
  // `handleTimerComplete` needs `switchTimer`. `switchTimer` needs `restart`. `restart` comes from `useTimer`.
  // `useTimer` needs `onExpire`.
  // We can use a ref for `onExpire` or `handleTimerComplete`.

  const handleTimerCompleteRef = useRef<() => void>(() => {});

  const { seconds, minutes, hours, isRunning, pause, restart } = useTimer({
    expiryTimestamp: getExpiryTimestamp(getTimerDuration(timerType)),
    onExpire: () => handleTimerCompleteRef.current(), // Use ref to break cycle
    autoStart: false,
  });

  // Now we can define `switchTimer` which uses `restart`
  // And `handleTimerComplete` which uses `switchTimer`.
  // And update the ref.

  // NOTE: In the original code, `handleTimerComplete` was defined AFTER `useTimer`.
  // And `useTimer` used `onExpire: () => handleTimerComplete()`.
  // This verifies that `handleTimerComplete` was NOT const-hoisted (it's const).
  // BUT the lambda `() => handleTimerComplete()` delays access until execution time.
  // So as long as we don't call it during render, it's fine.
  // Lint rules might complain about use-before-define though.

  // Let's stick to the structure but wrap in useCallback.
  // BUT `useCallback` relies on dependencies. `handleTimerComplete` depends on `switchTimer`.
  // `switchTimer` depends on `restart`. `restart` is available.
  // so:
  // 1. switchTimer (needs restart)
  // 2. showNotification
  // 3. handleTimerComplete (needs switchTimer, showNotification)
  // 4. Update ref or just rely on lazy closure execution?
  // The lint error `Block-scoped variable 'handleTimerComplete' used before its declaration` likely came because I added it to `useEffect` deps *before* it was defined.
  // Correct order:
  // useTimer -> switchTimer -> showNotification -> handleTimerComplete -> useEffect

  const switchTimerImpl = useCallback(
    (type: TimerType, autoStart = false) => {
      isUserInteracting.current = true;

      if (import.meta.env.DEV) console.log(`[Timer] Switching to ${type}, autoStart=${autoStart}`);

      const duration = getTimerDuration(type);
      if (import.meta.env.DEV)
        console.log(`[Timer] Duration for ${type}: ${duration} seconds (${duration / 60} minutes)`);

      if (duration <= 0) {
        console.error(`[Timer] Invalid duration: ${duration} seconds for ${type}`);
        return;
      }

      // Create expiry timestamp
      const time = new Date();
      time.setSeconds(time.getSeconds() + duration);

      if (import.meta.env.DEV)
        console.log(`[Timer] Expiry timestamp:`, time, `Current time:`, new Date());

      // Update UI state
      setTimerType(type);
      setPausedTimeSeconds(0);
      setHasBeenStarted(autoStart);

      // Store expiry time for visibility change recovery
      if (autoStart) {
        setExpiryTime(time);
      } else {
        setExpiryTime(null);
      }

      // Call restart with the autoStart parameter directly
      // This should update the display AND start if autoStart=true
      restart(time, autoStart);
      if (import.meta.env.DEV) console.log(`[Timer] Called restart with autoStart=${autoStart}`);

      // Clear guard after state updates complete
      setTimeout(() => {
        isUserInteracting.current = false;
      }, 150);
    },
    [getTimerDuration, restart]
  );

  const handleReset = useCallback(() => {
    isUserInteracting.current = true;
    setHasBeenStarted(false);
    setPausedTimeSeconds(0);
    const duration = getTimerDuration(timerType);
    restart(getExpiryTimestamp(duration), false);
    setTimeout(() => {
      isUserInteracting.current = false;
    }, 100);
  }, [timerType, getTimerDuration, getExpiryTimestamp, restart]);

  const handleStartPauseResume = useCallback(() => {
    isUserInteracting.current = true;

    if (isRunning) {
      // Currently running → Pause and save remaining time
      const remainingSeconds = minutes * 60 + seconds;
      setPausedTimeSeconds(remainingSeconds);
      wasRunningRef.current = false; // User explicitly paused
      pause();
    } else if (hasBeenStarted && pausedTimeSeconds > 0) {
      // Paused → Resume from saved time
      const newExpiry = getExpiryTimestamp(pausedTimeSeconds);
      setExpiryTime(newExpiry); // Store for visibility recovery
      wasRunningRef.current = true; // Track that we should be running
      restart(newExpiry, true);
    } else {
      // Initial state → Start fresh
      setHasBeenStarted(true);
      const newExpiry = getExpiryTimestamp(getTimerDuration(timerType));
      setExpiryTime(newExpiry); // Store for visibility recovery
      wasRunningRef.current = true; // Track that we should be running
      // Use restart() instead of start() to properly set the expiry timestamp
      restart(newExpiry, true);

      // Attempt to unlock audio context on first interaction
      if (soundEnabled) {
        // Lazy initialization if audioRef is missing
        if (!audioRef.current) {
          audioRef.current = new Audio(BELL_SOUND);
          audioRef.current.load();
        }

        if (audioRef.current) {
          // Play and pause immediately to unlock audio on iOS/Safari
          const audio = audioRef.current;
          audio
            .play()
            .then(() => {
              audio.pause();
              audio.currentTime = 0;
            })
            .catch(() => {
              // Playback failed (e.g. no user gesture) - harmless
            });
        }
      }
    }

    // Clear flag after state updates complete
    setTimeout(() => {
      isUserInteracting.current = false;
    }, 100);
  }, [
    isRunning,
    hasBeenStarted,
    minutes,
    seconds,
    pausedTimeSeconds,
    pause,
    restart,
    getExpiryTimestamp,
    getTimerDuration,
    timerType,
    soundEnabled,
  ]);

  // Read notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);

      const handlePermissionChange = () => {
        setNotificationPermission(Notification.permission);
      };

      window.addEventListener('notificationPermissionChange', handlePermissionChange);
      return () =>
        window.removeEventListener('notificationPermissionChange', handlePermissionChange);
    }
  }, []);

  // Update timer display when settings change
  useEffect(() => {
    if (!isRunning && !hasBeenStarted && !isUserInteracting.current) {
      const duration = getTimerDuration(timerType);
      restart(getExpiryTimestamp(duration), false);
    }
  }, [
    timers.pomodoro,
    timers.shortBreak,
    timers.longBreak,
    timerType,
    getTimerDuration,
    getExpiryTimestamp,
    restart,
    isRunning,
    hasBeenStarted,
  ]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Check if any modal is open - if so, disable timer shortcuts
      // This prevents conflict with Save (Cmd+S) in Settings
      const { isSettingsOpen, isShortcutModalOpen, isUserStatsOpen } = useSettingsStore.getState();
      if (isSettingsOpen || isShortcutModalOpen || isUserStatsOpen) {
        return;
      }

      // Ctrl/Alt + S to start/pause/resume timer
      if (e.key.toLowerCase() === 's' && (e.ctrlKey || e.altKey)) {
        e.preventDefault();
        handleStartPauseResume();
        return;
      }

      // Ctrl/Alt + R to reset timer
      if (e.key.toLowerCase() === 'r' && (e.ctrlKey || e.altKey)) {
        e.preventDefault();
        handleReset();
        return;
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleStartPauseResume, handleReset]);

  // Flash cleanup
  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
    };
  }, []);

  const showNotification = useCallback(
    (type: TimerType) => {
      if (import.meta.env.DEV) {
        console.log('[Notification] showNotification called', {
          type,
          isDiscordActivity,
          notificationInWindow: 'Notification' in window,
          notificationPermission,
          timestamp: new Date().toISOString(),
        });
      }

      // Only show browser notifications on web (not Discord Activity)
      if (isDiscordActivity) {
        if (import.meta.env.DEV) console.log('[Notification] Blocked: running in Discord Activity');
        return;
      }

      if (!('Notification' in window)) {
        if (import.meta.env.DEV)
          console.log('[Notification] Blocked: Notification API not available');
        return;
      }

      if (notificationPermission !== 'granted') {
        if (import.meta.env.DEV) {
          console.log('[Notification] Blocked: permission not granted', { notificationPermission });
        }
        return;
      }

      try {
        const titles = {
          pomodoro: '🍅 Pomodoro Complete!',
          shortBreak: '☕ Short Break Over!',
          longBreak: '🎉 Long Break Over!',
        };

        const bodies = {
          pomodoro: 'Great work! Time for a break.',
          shortBreak: 'Break time is over. Ready to focus?',
          longBreak: "Feeling refreshed? Let's get back to work!",
        };

        if (import.meta.env.DEV) {
          console.log('[Notification] Creating notification', {
            title: titles[type],
            body: bodies[type],
          });
        }

        const notification = new Notification(titles[type], {
          body: bodies[type],
          icon: '/pomodoro.svg',
          badge: '/pomodoro.svg',
          tag: 'pomodoro-timer',
          requireInteraction: false,
        });

        if (import.meta.env.DEV)
          console.log('[Notification] ✓ Notification created successfully', notification);

        // Auto-close after 10 seconds
        setTimeout(() => notification.close(), 10000);
      } catch (err) {
        console.error('[Notification] ✗ Failed to create notification:', err);
      }
    },
    [isDiscordActivity, notificationPermission]
  );

  const handleTimerComplete = useCallback(() => {
    // Show notification
    showNotification(timerType);

    // Play completion sound
    if (soundEnabled) {
      if (audioRef.current) {
        audioRef.current.volume = volume / 100;
        audioRef.current.currentTime = 0; // Reset playback position
        audioRef.current.play().catch((e) => {
          if (import.meta.env.DEV) console.log('Audio playback failed:', e);
          // Try to reset if playback failed
          audioRef.current?.load();
        });
      } else {
        if (import.meta.env.DEV) console.warn('[Timer] Audio ref missing, skipping sound.');
      }
    }

    // Visual flash effect
    setIsFlashing(true);
    // Clear any existing flash timeout
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
    }
    flashTimeoutRef.current = setTimeout(() => setIsFlashing(false), 1000);

    // Award XP based on timer type
    if (timerType === 'pomodoro') {
      const durationMinutes = timers.pomodoro;

      // Update local state and save to database
      addXP(durationMinutes);
      setPomodoroCount((prev) => prev + 1);
    } else if (timerType === 'shortBreak' || timerType === 'longBreak') {
      // Award XP for breaks
      const durationMinutes = timerType === 'shortBreak' ? timers.shortBreak : timers.longBreak;
      const xpEarned = durationMinutes * XP_PER_MINUTE_BREAK;

      if (import.meta.env.DEV) {
        console.log(
          `[Timer] ${timerType} complete! Awarding ${xpEarned} XP (${durationMinutes} min × ${XP_PER_MINUTE_BREAK} XP/min)`
        );
      }

      const currentState = useSettingsStore.getState();
      useSettingsStore.setState({
        xp: currentState.xp + xpEarned,
      });

      // Save to database
      if (appUser?.id && appUser?.discord_id) {
        if (import.meta.env.DEV) {
          console.log('[Timer] Saving break to database...', {
            userId: appUser.id,
            discordId: appUser.discord_id,
            breakType: timerType === 'shortBreak' ? 'short' : 'long',
            duration: durationMinutes,
            xp: xpEarned,
          });
        }

        const sessionStartedAt = Date.now() - durationMinutes * 60 * 1000;

        import('../../lib/retryUtils').then(({ withRetryAsync }) => {
          import('../../lib/syncQueue').then(({ queueBreakOperation }) => {
            withRetryAsync(
              () =>
                saveCompletedBreak(appUser.id, {
                  break_type: timerType === 'shortBreak' ? 'short' : 'long',
                  duration_minutes: durationMinutes,
                  xp_earned: xpEarned,
                }),
              {
                context: {
                  operation: 'saveCompletedBreak',
                  userId: appUser.id,
                  breakType: timerType,
                  durationMinutes,
                  xpEarned,
                },
              }
            ).then((result) => {
              if (result) {
                if (import.meta.env.DEV)
                  console.log('[Timer] ✓ Break saved to database successfully');
              } else {
                if (import.meta.env.DEV) console.log('[Timer] Queuing break for later sync');
                queueBreakOperation(
                  appUser.id,
                  {
                    break_type: timerType === 'shortBreak' ? 'short' : 'long',
                    duration_minutes: durationMinutes,
                    xp_earned: xpEarned,
                  },
                  sessionStartedAt
                );
              }
            });
          });
        });
      } else {
        console.warn('[Timer] Cannot save break - user not authenticated', { appUser });
      }
    }

    const currentSettings = useSettingsStore.getState();

    if (import.meta.env.DEV) {
      console.log('[Timer] Current settings:', {
        autoStartBreaks: currentSettings.autoStartBreaks,
        autoStartPomodoros: currentSettings.autoStartPomodoros,
        pomodoroCount: pomodoroCount,
        pomodorosBeforeLongBreak: pomodorosBeforeLongBreak,
      });
    }

    let nextType: TimerType;
    if (timerType === 'pomodoro') {
      const isLongBreakTime = (pomodoroCount + 1) % pomodorosBeforeLongBreak === 0;
      if (isLongBreakTime) {
        nextType = 'longBreak';
        if (import.meta.env.DEV)
          console.log(
            '[Timer] Pomodoro complete! Next: Long Break (completed',
            pomodoroCount + 1,
            'pomodoros)'
          );
      } else {
        nextType = 'shortBreak';
        if (import.meta.env.DEV)
          console.log(
            '[Timer] Pomodoro complete! Next: Short Break (completed',
            pomodoroCount + 1,
            'pomodoros)'
          );
      }

      setTimeout(() => {
        if (currentSettings.autoStartBreaks) {
          if (import.meta.env.DEV)
            console.log('[Timer] Auto-start breaks ENABLED → starting break automatically');
          switchTimerImpl(nextType, true);
        } else {
          if (import.meta.env.DEV)
            console.log('[Timer] Auto-start breaks DISABLED → break ready but not started');
          switchTimerImpl(nextType, false);
        }
      }, 100);
    } else {
      nextType = 'pomodoro';
      if (import.meta.env.DEV) console.log('[Timer] Break complete! Next: Pomodoro');

      setTimeout(() => {
        if (currentSettings.autoStartPomodoros) {
          if (import.meta.env.DEV)
            console.log('[Timer] Auto-start pomodoros ENABLED → starting pomodoro automatically');
          switchTimerImpl(nextType, true);
        } else {
          if (import.meta.env.DEV)
            console.log('[Timer] Auto-start pomodoros DISABLED → pomodoro ready but not started');
          switchTimerImpl(nextType, false);
        }
      }, 100);
    }
  }, [
    showNotification,
    timerType,
    soundEnabled,
    volume,
    timers,
    addXP,
    pomodoroCount,
    pomodorosBeforeLongBreak,
    switchTimerImpl,
    appUser,
  ]);

  // Update ref for useTimer
  useEffect(() => {
    handleTimerCompleteRef.current = handleTimerComplete;
  }, [handleTimerComplete]);

  // Visibility change handler - resync timer when returning from background
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Use wasRunningRef instead of isRunning to avoid stale state
      if (document.visibilityState === 'visible' && expiryTime && wasRunningRef.current) {
        const now = new Date();
        const remainingMs = expiryTime.getTime() - now.getTime();

        if (remainingMs > 0) {
          if (import.meta.env.DEV) {
            console.log(
              '[Timer] Visibility restored, resyncing timer. Remaining:',
              Math.floor(remainingMs / 1000),
              'seconds'
            );
          }
          restart(expiryTime, true);
        } else {
          if (import.meta.env.DEV) {
            console.log('[Timer] Timer expired while in background, triggering completion');
          }
          wasRunningRef.current = false;
          handleTimerComplete();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [expiryTime, restart, handleTimerComplete]);

  const formatTime = (hours: number, mins: number, secs: number) => {
    const totalMinutes = hours * 60 + mins;
    return `${String(totalMinutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div data-tour="timer-section" className="flex flex-col items-center justify-center space-y-6">
      {/* Timer Type Selector - Hidden in PIP mode */}
      {!isPIPMode && (
        <div
          className="flex gap-2 sm:gap-3 p-1 bg-black/20 rounded-xl backdrop-blur-sm"
          role="tablist"
          aria-label="Timer type selector"
        >
          <button
            onClick={() => switchTimerImpl('pomodoro')}
            role="tab"
            aria-selected={timerType === 'pomodoro'}
            aria-label="Pomodoro timer"
            className={`px-3 py-1.5 sm:px-6 sm:py-2 rounded-lg font-medium text-sm sm:text-base transition-all ${
              timerType === 'pomodoro'
                ? 'bg-white text-gray-900 shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            Pomodoro
          </button>
          <button
            onClick={() => switchTimerImpl('shortBreak')}
            role="tab"
            aria-selected={timerType === 'shortBreak'}
            aria-label="Short break timer"
            className={`px-3 py-1.5 sm:px-6 sm:py-2 rounded-lg font-medium text-sm sm:text-base transition-all ${
              timerType === 'shortBreak'
                ? 'bg-white text-gray-900 shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            Short Break
          </button>
          <button
            onClick={() => switchTimerImpl('longBreak')}
            role="tab"
            aria-selected={timerType === 'longBreak'}
            aria-label="Long break timer"
            className={`px-3 py-1.5 sm:px-6 sm:py-2 rounded-lg font-medium text-sm sm:text-base transition-all ${
              timerType === 'longBreak'
                ? 'bg-white text-gray-900 shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            Long Break
          </button>
        </div>
      )}

      {/* Timer Display */}
      <div
        className={`font-bold text-white tracking-wider transition-all duration-300 ${
          isFlashing ? 'scale-110 text-yellow-400 drop-shadow-[0_0_20px_rgba(250,204,21,0.5)]' : ''
        }`}
        style={{
          fontSize: isPIPMode
            ? '3rem'
            : isMobile || isTablet
              ? '18vmin'
              : 'clamp(6rem, 12vw, 12rem)',
        }}
        role="timer"
        aria-live="off"
        aria-label={`${formatTime(hours, minutes, seconds)} remaining`}
      >
        {formatTime(hours, minutes, seconds)}
      </div>

      {/* Control Buttons - Hidden in PIP mode */}
      {!isPIPMode && (
        <div className="flex gap-3 sm:gap-4">
          <button
            onClick={handleStartPauseResume}
            aria-label={isRunning ? 'Pause timer' : hasBeenStarted ? 'Resume timer' : 'Start timer'}
            className="px-6 py-2 sm:px-8 sm:py-3 bg-white text-gray-900 rounded-lg font-bold text-lg sm:text-xl hover:bg-gray-100 transition-colors shadow-lg active:scale-95 transform"
          >
            {isRunning ? 'Pause' : hasBeenStarted ? 'Resume' : 'Start'}
          </button>
          <button
            onClick={handleReset}
            aria-label="Reset timer"
            className="px-6 py-2 sm:px-8 sm:py-3 bg-white/20 text-white rounded-lg font-bold text-lg sm:text-xl hover:bg-white/30 transition-colors backdrop-blur-sm active:scale-95 transform"
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
});
